import { IncomingHttpHeaders, IncomingMessage } from 'http';

import { LaunchApiParams } from '../../pages/_app';
import SimpleStore from '../../store/SimpleStore';
import { getDeviceType } from '../../utils/CheckDeviceType';
import { DynamicObject } from '../../utils/commonTypes';
import {
  B2BAPI_COUNTRY_URL,
  SINGLE_LAUNCH_API
} from '../../utils/constants/ApiConstants';
import { DEVICE_TYPES } from '../../utils/constants/AppConstants';
import { HTTP_METHODS } from '../../utils/constants/HttpMethodsConstants';
import { LAUNCH_ERROR } from '../../utils/constants/stringConstants';
import { buildUrlWithQueryParams } from '../../utils/UrlGenerator';
import {
  getUserLocationFromAkamaiEdgescape,
  getUserLocationObject
} from '../../utils/user-location-utils/UpdateUserLocation';
import { getPlatformName } from '../../utils/UtilityMethods';
import { HttpService } from '../fetch/HttpService';
import { SingleLaunchResponse } from '../responseTypes/singleLaunchResponse';
import { XtraApiResponse } from '../responseTypes/xtraApiResponse';

export const httpService = new HttpService();

export const getUserLocation = async (
  ip: string | string[],
  countryCookieValue: string,
  akamaiHeaders?: string | string[]
): Promise<XtraApiResponse> => {
  let locationRes: XtraApiResponse;
  try {
    const locationUrl = (
      ip ? B2BAPI_COUNTRY_URL + '?ip=' + ip : B2BAPI_COUNTRY_URL
    ) as string;
    locationRes = await httpService.fetchApiData({
      url: locationUrl,
      type: HTTP_METHODS.GET
    });
  } catch (error) {
    console.error('Error while fetching users location', error);
    SimpleStore.set(LAUNCH_ERROR, error as DynamicObject);
    // Read location from akamai Header
    const userLocationHeader = akamaiHeaders;
    const locationFromEdgescape = userLocationHeader
      ? (locationRes = getUserLocationFromAkamaiEdgescape(
          userLocationHeader as string
        ))
      : null;

    if (locationFromEdgescape?.country_code) {
      locationRes = locationFromEdgescape;
    } else {
      // Read location from cookie
      locationRes = getUserLocationObject(countryCookieValue);
    }
  }
  return locationRes;
};
/**
 * Fetches launch API data based on the provided location response.
 *
 * @param locationRes - The country API response
 * @returns A promise that resolves to the single launch response or undefined if an error occurs.
 */
export const fetchLaunchAPIData = async (
  locationRes: XtraApiResponse
): Promise<SingleLaunchResponse | undefined> => {
  try {
    const launchApiParams: LaunchApiParams = {
      state: locationRes?.state_code || '',
      ccode: locationRes?.country_code || '',
      country: locationRes?.country_code || '',
      translation: 'en',
      version: 4.0,
      platform_name: getPlatformName()
    };
    let singleLaunchUrl = SINGLE_LAUNCH_API;
    singleLaunchUrl = buildUrlWithQueryParams(
      singleLaunchUrl as string,
      launchApiParams
    );
    const singleLaunchResponse: SingleLaunchResponse =
      await httpService.fetchApiData({
        url: singleLaunchUrl,
        type: HTTP_METHODS.GET
      });
    return singleLaunchResponse;
  } catch (error) {
    SimpleStore.set(LAUNCH_ERROR, error as DynamicObject);
    console.error('Error while fetching Single Launch response', error);
    return;
  }
};

/**
 * Returns the device type based on the provided headers.
 *
 * @param headers - The headers object containing the user-agent and x-device-* headers.
 * @returns The device type (mobile, desktop, or tablet).
 */
export const getTrueDeviceType = (headers: IncomingHttpHeaders | undefined) => {
  let deviceType = DEVICE_TYPES.MOBILE;
  if (headers) {
    const userAgent = headers['user-agent'] as string;
    deviceType = getDeviceType(userAgent);

    //Set device types from akamai header
    const mobileHeader = headers['x-device-mobile'] as string;
    const tabletHeader = headers['x-device-tablet'] as string;
    if (mobileHeader && tabletHeader) {
      const mobileDevice = /true/.test(mobileHeader);
      const tabletDevice = /true/.test(tabletHeader);

      if (!tabletDevice && !mobileDevice) {
        deviceType = DEVICE_TYPES.DESKTOP;
      } else if (!tabletDevice && mobileDevice) {
        deviceType = DEVICE_TYPES.MOBILE;
      }
      if (tabletDevice && mobileDevice) {
        deviceType = DEVICE_TYPES.TABLET;
      }
    }
  }
  return deviceType;
};

/**
 * Checks if the client supports the WebP image format.
 *
 * @param requestHeaders - The incoming HTTP headers from the client.
 * @returns A boolean value indicating whether the client supports WebP or not.
 */
export const checkWebpSupport = (
  requestHeaders: IncomingMessage | undefined
) => {
  //set webpFlags oon server
  let isWebPCompatible = false;
  if (requestHeaders?.headers.accept?.includes('image/webp')) {
    const globalServer = global as typeof globalThis & {
      WEBP_COMPATIBLE: boolean;
    };
    globalServer.WEBP_COMPATIBLE = true;
    isWebPCompatible = true;
  }

  return isWebPCompatible;
};

export const checkAVIFSuport = (
  requestHeaders: IncomingMessage | undefined
) => {
  //set webpFlags oon server
  if (requestHeaders?.headers.accept?.includes('image/avif')) {
    const globalServer = global as typeof globalThis & {
      AVIF_COMPATIBLE: boolean;
    };
    globalServer.AVIF_COMPATIBLE = true;
    return true;
  }
  return false;
};
